import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Flex,
  Center,
  Spinner,
  VisuallyHidden,
} from '@chakra-ui/react';
import {
  HiShieldCheck,
  HiMapPin,
  HiClock,
  HiArrowLeft,
  HiUserGroup,
  HiAcademicCap,
  HiCheckCircle,
  HiExclamationTriangle,
  HiPhone,
  HiIdentification,
} from 'react-icons/hi2';
import { Button, StatCard } from '@themes/aegix';
import { OnePageHeader as PageHeader } from '@/components/layout/OnePageHeader';
import { useNotificationsDrawer } from '@/contexts/NotificationsDrawerContext';
import { useUnreadCount } from '@/hooks/useReunification';
import { StudentStatusBadge, GuardianStatusBadge } from '@/components/StudentStatusBadge';
import type { StudentStatus, GuardianStatus } from '@/types/reunification';
import { STUDENT_STATUS_LABELS } from '@/types/reunification';
import type { StudentReunification } from '@/services/reunification';
import {
  useEventDetail,
  useEventStudents,
  useGuardianSelf,
  useMyStudents,
  usePreVerifySelf,
  useMarkArrived,
  useCheckInSelf,
  useReportSafeOffsite,
} from '@/hooks/useReunification';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/** Ordered student status flow from ADR-064 (backend lowercase). */
const STUDENT_STATUS_ORDER: StudentStatus[] = [
  'secured_at_incident',
  'transported',
  'arrived_at_site',
  'in_assembly',
  'cleared_for_release',
  'in_reunification_room',
  'reunified',
  'exited',
];

function StatusTimeline({ current }: { current: StudentStatus }) {
  const { t } = useTranslation();
  const currentIdx = STUDENT_STATUS_ORDER.indexOf(current);
  // For offsite statuses, show them outside the main flow
  const isOffsite = currentIdx === -1;

  return (
    <Box>
      {isOffsite ? (
        <HStack gap={2} py={2}>
          <Box w={3} h={3} borderRadius="full" bg="green.500" aria-hidden="true" />
          <Text fontSize="sm" fontWeight="600" color="green.600">
            {t(`status.${current}`, STUDENT_STATUS_LABELS[current])}
          </Text>
        </HStack>
      ) : (
        <VStack align="stretch" gap={0}>
          {STUDENT_STATUS_ORDER.map((status, idx) => {
            const isPast = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            const isFuture = idx > currentIdx;

            return (
              <HStack key={status} gap={3} py={1.5} aria-label={`${t(`status.${status}`, STUDENT_STATUS_LABELS[status])}: ${isPast ? 'completed' : isCurrent ? 'current' : 'pending'}`}>
                {/* Dot + connector line */}
                <VStack gap={0} w={4} align="center" aria-hidden="true">
                  <Box
                    w={isCurrent ? 3.5 : 2.5}
                    h={isCurrent ? 3.5 : 2.5}
                    borderRadius="full"
                    bg={isPast ? 'green.500' : isCurrent ? 'blue.500' : 'neutral.300'}
                    border={isCurrent ? '2px solid' : 'none'}
                    borderColor={isCurrent ? 'blue.200' : 'transparent'}
                    transition="all 200ms"
                  />
                  {idx < STUDENT_STATUS_ORDER.length - 1 && (
                    <Box
                      w="2px"
                      h={3}
                      bg={isPast ? 'green.300' : 'neutral.200'}
                    />
                  )}
                </VStack>
                <Text
                  fontSize="sm"
                  fontWeight={isCurrent ? '600' : '400'}
                  color={
                    isFuture
                      ? 'text.secondary'
                      : isCurrent
                        ? 'text.primary'
                        : 'green.600'
                  }
                >
                  {t(`status.${status}`, STUDENT_STATUS_LABELS[status])}
                </Text>
              </HStack>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}

function StudentCard({
  student,
  isMine,
  onCheckIn,
  onReportOffsite,
}: {
  student: StudentReunification;
  isMine: boolean;
  onCheckIn: () => void;
  onReportOffsite: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const status = student.status;

  return (
    <Box
      bg="surface.primary"
      border="1px solid"
      borderColor={isMine ? 'border.action' : 'border.secondary'}
      borderRadius="lg"
      overflow="hidden"
    >
      {isMine && <Box h={1} bg="button.primary" />}
      <Box
        p={4}
        cursor="pointer"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${student.student_name} details`}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
        _hover={{ bg: 'surface.secondary' }}
        transition="background 200ms"
      >
        <Flex justify="space-between" align="center">
          <Box>
            <HStack gap={2}>
              <Text fontSize="md" fontWeight="600" color="text.primary">
                {student.student_name}
              </Text>
              {isMine && (
                <Badge bg="teal.100" color="teal.700" px={2} borderRadius="md" fontSize="xs">
                  {t('eventDetail.myChild')}
                </Badge>
              )}
            </HStack>
            <Text fontSize="sm" color="text.secondary">
              {student.grade} {student.homeroom ? `\u2014 ${student.homeroom}` : ''}
            </Text>
          </Box>
          <StudentStatusBadge status={status} />
        </Flex>
      </Box>

      {expanded && (
        <Box px={4} pb={4} borderTopWidth="1px" borderColor="border.line">
          <Box py={3}>
            <Text fontSize="xs" fontWeight="600" color="text.secondary" mb={2}>
              {t('eventDetail.statusTimeline')}
            </Text>
            <StatusTimeline current={status} />
          </Box>

          <Text fontSize="xs" color="text.secondary" mb={3}>
            {t('eventDetail.lastUpdated', {
              time: new Date(student.updated_at).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })
            })}
          </Text>

          {/* Actions for guardian's own children */}
          {isMine && (
            <HStack gap={2}>
              {status === 'cleared_for_release' ||
              status === 'in_reunification_room' ? (
                <Button size="sm" onClick={onCheckIn}>
                  {t('eventDetail.checkInForPickup')}
                </Button>
              ) : null}
              {status !== 'reunified' &&
                status !== 'exited' &&
                status !== 'reported_safe_offsite' && (
                  <Button
                    size="sm"
                    visual="secondary"
                    onClick={onReportOffsite}
                  >
                    {t('eventDetail.reportSafeOffsite')}
                  </Button>
                )}
            </HStack>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function EventDetail() {
  const { t } = useTranslation();
  useDocumentTitle(t('eventDetail.title'));
  const { open: openNotifications } = useNotificationsDrawer();
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.unread_count ?? 0;
  const { eventId } = useParams<{ eventId: string }>();

  const { data: event, isLoading: eventLoading } = useEventDetail(eventId);
  const { data: studentsData, isLoading: studentsLoading } = useEventStudents(eventId);
  const { data: guardianSelf } = useGuardianSelf(eventId);
  const { data: myStudentsData, isLoading: myStudentsLoading } = useMyStudents();

  const preVerify = usePreVerifySelf(eventId ?? '');
  const arrive = useMarkArrived(eventId ?? '');
  const checkIn = useCheckInSelf(eventId ?? '');
  const reportOffsite = useReportSafeOffsite(eventId ?? '');

  // Gate on the linked-students query too, so student cards never flash
  // as not-mine before the guardian's own children are known.
  const isLoading = eventLoading || studentsLoading || myStudentsLoading;

  if (isLoading) {
    return (
      <Box>
        <PageHeader
          icon={HiShieldCheck}
          title={t('eventDetail.title')}
          subtitle={t('eventDetail.loading')}
          showSearch={false}
          showNotifications={true}
          onNotificationClick={openNotifications}
          notificationCount={unreadCount}
          showColorMode={false}
          actions={
            <Link to="/events">
              <Button size="sm" visual="secondary">
                <HiArrowLeft size={16} />
                <Text ml={1}>{t('eventDetail.back')}</Text>
              </Button>
            </Link>
          }
        />
        <Center py={12} aria-live="polite" role="status">
          <Spinner size="lg" />
          <VisuallyHidden>{t('eventDetail.loadingDetails')}</VisuallyHidden>
        </Center>
      </Box>
    );
  }

  if (!event) {
    return (
      <Box p={8} textAlign="center">
        <Text fontSize="lg" color="text.secondary">
          {t('eventDetail.notFound')}
        </Text>
        <Link to="/events">
          <Button size="sm" mt={4}>
            {t('eventDetail.backToEvents')}
          </Button>
        </Link>
      </Box>
    );
  }

  const studentList = studentsData ?? [];
  const stats = event.stats;
  const guardianStatus: GuardianStatus = guardianSelf?.status ?? 'notified';

  // "My" students: the guardian's linked students (OneUserLink-backed
  // GET /reunification/guardian/students/) are keyed by RosterPerson
  // public_id; event student rows carry roster_person_public_id
  // precisely so the two id spaces can be joined here.
  const myStudentIds = new Set(
    (myStudentsData?.results ?? []).map((s) => s.public_id),
  );

  return (
    <Box>
      <PageHeader
        icon={HiShieldCheck}
        title={`${event.school_name} \u2014 ${event.incident_type_display}`}
        subtitle={`${event.status_display}`}
        showSearch={false}
        showNotifications={false}
        showColorMode={false}
        actions={
          <Link to="/events">
            <Button size="sm" visual="secondary">
              <HiArrowLeft size={16} />
              <Text ml={1}>{t('eventDetail.back')}</Text>
            </Button>
          </Link>
        }
      />

      <Box p={{ base: 4, md: 6 }}>
        {/* Event overview stats */}
        <SimpleGrid columns={{ base: 2, md: 4 }} gap={4} mb={6}>
          <StatCard
            label={t('dashboard.totalStudents')}
            value={stats.total_students}
            icon={HiAcademicCap}
            colorPalette="blue"
          />
          <StatCard
            label={t('eventDetail.reunified')}
            value={stats.reunified}
            icon={HiCheckCircle}
            colorPalette="green"
          />
          <StatCard
            label={t('eventDetail.accounted')}
            value={stats.accounted}
            icon={HiUserGroup}
            colorPalette="teal"
          />
          <StatCard
            label={t('eventDetail.unaccounted')}
            value={stats.unaccounted}
            icon={HiIdentification}
            colorPalette={stats.unaccounted > 0 ? 'red' : 'purple'}
          />
        </SimpleGrid>

        {/* Site information */}
        <Box
          bg="surface.primary"
          border="1px solid"
          borderColor="border.secondary"
          borderRadius="lg"
          p={4}
          mb={6}
        >
          <Text fontSize="sm" fontWeight="600" color="text.primary" mb={2}>
            {t('eventDetail.reunificationSite')}
          </Text>
          {event.primary_site_name && (
            <HStack gap={2} mb={1}>
              <HiMapPin size={16} />
              <Text fontSize="sm" color="text.primary">
                {event.primary_site_name}
              </Text>
            </HStack>
          )}
          {event.plan_name && (
            <Text fontSize="sm" color="text.secondary" ml={6} mb={1}>
              {t('eventDetail.planLabel', { name: event.plan_name })}
            </Text>
          )}
          {event.started_at && (
            <HStack gap={2} mt={2}>
              <HiClock size={16} />
              <Text fontSize="sm" color="text.secondary">
                {t('eventDetail.activatedAt', {
                  time: new Date(event.started_at).toLocaleString([], {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                })}
              </Text>
            </HStack>
          )}
        </Box>

        {/* Guardian check-in workflow */}
        {guardianSelf && (
          <Box
            bg="surface.primary"
            border="1px solid"
            borderColor="border.action"
            borderRadius="lg"
            p={4}
            mb={6}
          >
            <HStack gap={2} mb={3}>
              <HiExclamationTriangle size={18} color="var(--chakra-colors-orange-500)" />
              <Text fontSize="sm" fontWeight="600" color="text.primary">
                {t('eventDetail.yourCheckinStatus')}
              </Text>
            </HStack>

            <Box mb={3}>
              <GuardianStatusBadge status={guardianStatus} />
            </Box>

            <HStack gap={2} flexWrap="wrap">
              {guardianStatus === 'notified' && (
                <Button
                  size="sm"
                  onClick={() => preVerify.mutate()}
                  disabled={preVerify.isPending}
                >
                  <HiIdentification size={16} />
                  <Text ml={1}>{t('eventDetail.preVerifyId')}</Text>
                </Button>
              )}
              {(guardianStatus === 'notified' ||
                guardianStatus === 'pre_verified') && (
                <Button
                  size="sm"
                  onClick={() => arrive.mutate()}
                  disabled={arrive.isPending}
                >
                  <HiMapPin size={16} />
                  <Text ml={1}>{t('eventDetail.iHaveArrived')}</Text>
                </Button>
              )}
              {guardianStatus === 'arrived' && (
                <Button
                  size="sm"
                  onClick={() => checkIn.mutate()}
                  disabled={checkIn.isPending}
                >
                  <HiCheckCircle size={16} />
                  <Text ml={1}>{t('eventDetail.checkIn')}</Text>
                </Button>
              )}
              {guardianStatus === 'checked_in' && (
                <HStack gap={2}>
                  <HiPhone size={16} />
                  <Text fontSize="sm" color="text.secondary">
                    {t('eventDetail.waitingMatch')}
                  </Text>
                </HStack>
              )}
              {guardianStatus === 'reunified' && (
                <Text fontSize="sm" color="green.600" fontWeight="600">
                  {t('eventDetail.completeNotice')}
                </Text>
              )}
            </HStack>
          </Box>
        )}

        {/* Student list */}
        <Box>
          <Text fontSize="md" fontWeight="600" color="text.primary" mb={3}>
            {t('eventDetail.studentsHeader', { count: studentList.length })}
          </Text>
          <VStack gap={3} align="stretch">
            {studentList.map((student) => (
              <StudentCard
                key={student.public_id}
                student={student}
                isMine={
                  student.roster_person_public_id != null &&
                  myStudentIds.has(student.roster_person_public_id)
                }
                onCheckIn={() => checkIn.mutate()}
                onReportOffsite={() =>
                  reportOffsite.mutate({
                    studentId: student.public_id,
                    data: {},
                  })
                }
              />
            ))}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
}
